import {useEffect, useMemo, useRef} from 'react';
import {throttle, useTimeoutRef} from '@chekin/core';

const DEFAULT_EVENTS = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
const DEFAULT_INACTIVITY_LIMIT = 120 * 1000;
const DEFAULT_THROTTLE_MS = 1000;

interface Props {
  enabled: boolean;
  onIdle: () => void;
  inactivityLimit?: number;
  throttleMs?: number;
  events?: string[];
}

export function useIdleDetector({
  enabled,
  onIdle,
  inactivityLimit = DEFAULT_INACTIVITY_LIMIT,
  throttleMs = DEFAULT_THROTTLE_MS,
  events = DEFAULT_EVENTS,
}: Props): void {
  const timeoutRef = useTimeoutRef();
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const reportActivity = useMemo(
    () =>
      throttle(() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        if (enabled) {
          timeoutRef.current = setTimeout(() => onIdleRef.current(), inactivityLimit);
        }
      }, throttleMs),
    [enabled, inactivityLimit, throttleMs, timeoutRef],
  );

  useEffect(
    function addEventListeners() {
      if (enabled) {
        events.forEach(event => {
          window.addEventListener(event, reportActivity, {passive: true});
        });
      }

      return () => {
        events.forEach(event => {
          window.removeEventListener(event, reportActivity);
        });
      };
    },
    [enabled, reportActivity, events],
  );

  useEffect(
    function startTimer() {
      clearTimeout(timeoutRef.current);

      if (enabled) {
        timeoutRef.current = setTimeout(() => onIdleRef.current(), inactivityLimit);
      }
    },
    [enabled, inactivityLimit, timeoutRef],
  );
}
